import { onAuthStateChanged, signInWithCustomToken, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import React, { useContext, useEffect, useReducer } from 'react';
import useErrorReporter from 'web/hooks/useErrorReporter';
import { firestoreUserConverter } from 'web/utils/convert';
import FirebaseContext from './FirebaseContext';
import useFirestoreLazy from './FirebaseContext/useFirestoreLazy';
import useAuth from './FirebaseContext/useAuth';
import useFunctions from './FirebaseContext/useFunctions';
import UserContext, { initialUserContext } from './UserContext';
import { userReducer } from './userState';

const UserProvider = ({
  children,
  preloadFirestore = false,
}: {
  children: React.ReactNode;
  preloadFirestore?: boolean;
}) => {
  const auth = useAuth();
  const firebase = useContext(FirebaseContext);
  const functions = useFunctions();
  const errorReporter = useErrorReporter();
  const [state, dispatch] = useReducer(userReducer, {
    ...initialUserContext,
    user: auth.currentUser?.tenantId ? null : auth.currentUser,
  });
  const lazyFirestore = useFirestoreLazy({ enabled: Boolean(state.user?.uid), preload: preloadFirestore });

  useEffect(() => {
    return onAuthStateChanged(
      auth,
      (nextUser) => {
        if (nextUser?.tenantId) {
          // Booking page clients sign in to their tenant's own app. A tenant user persisted on the default app
          // by an older release is signed out so they log in again through the portal.
          void signOut(auth).catch((err) => errorReporter.report(`Failed to sign out a legacy tenant user: ${err}`));
          dispatch({
            type: 'logout',
          });
        } else if (nextUser) {
          dispatch({
            type: 'login',
            user: nextUser,
          });
        } else {
          dispatch({
            type: 'logout',
          });
        }
      },
      (err) => errorReporter.report(`Error while handling auth state: ${err}`),
    );
  }, [errorReporter, auth]);

  useEffect(() => {
    if (state.user?.uid && lazyFirestore) {
      return lazyFirestore.onSnapshot(
        lazyFirestore.doc(
          lazyFirestore.collection(lazyFirestore.getFirestore(firebase), 'users').withConverter(firestoreUserConverter),
          state.user.uid,
        ),
        (snapshot) => {
          if (!snapshot.exists()) {
            // Freshly created user accounts won't have a user document created - we ignore the error in this case
            if (Date.parse(state.user.metadata.creationTime) < Date.now() - 5000) {
              errorReporter.report("Error while loading user data: user doc doesn't exist.");
            }
          } else if (!snapshot.data()) {
            errorReporter.report('Error while loading user data: user doc is empty.');
          } else {
            dispatch({
              type: 'load-data',
              userData: snapshot.data(),
            });
          }
        },
        (err) => errorReporter.report(`Error while loading user data: ${err}`),
      );
    }
  }, [errorReporter, firebase, lazyFirestore, state.user]);

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const idTokenResult = await state.user.getIdTokenResult();
        dispatch({
          type: 'load-claims',
          claims: idTokenResult.claims,
        });
      } catch (err) {
        errorReporter.report(`Error while getting user claims: ${err}`);
      }
    };
    if (state.user) {
      void checkAdmin();
    }
  }, [errorReporter, state.user]);

  useEffect(() => {
    if (state.claims && state.claims.suUid && state.claims.suExp) {
      const timeoutMs = new Date(Number(state.claims.suExp)).getTime() - Date.now();
      const timeoutId = setTimeout(() => {
        const logout = async () => {
          // Try to switch back to the original user
          try {
            const res = await httpsCallable<unknown, { token: string }>(
              functions,
              'authLoginSubstituteUser',
            )({
              uid: state.claims.suUid,
            });
            const { token } = res.data;
            await signInWithCustomToken(auth, token);
          } catch (err) {
            errorReporter.report(`Failed to switch back from a substituted user on timeout: ${err}`);
            console.error(`Failed to switch back from a substituted user on timeout. Logging out instead.`);
            await signOut(auth);
            // we don't check error here, just let the app crash
          }
        };
        void logout();
      }, timeoutMs);
      return () => clearTimeout(timeoutId);
    }
  }, [errorReporter, auth, state.claims, functions]);

  useEffect(() => {
    errorReporter.setUser(state.user?.uid || undefined);
  }, [errorReporter, state.user?.uid]);

  return <UserContext.Provider value={state}>{children}</UserContext.Provider>;
};

export default UserProvider;
